import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, withRetry } from '../lib/supabase';
import { applyDemoPhoto, registerDemoUser } from '../lib/demoPhotos';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [roles, setRoles] = useState([]);
  const [activeRole, setActiveRole] = useState(null);
  const [loading, setLoading] = useState(true);

  // Listen for auth state changes
  useEffect(() => {
    let cancelled = false;

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (cancelled) return;
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
        fetchRoles(session.user.id);
      } else {
        setLoading(false);
      }
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });

    // Subscribe to auth changes
    // IMPORTANT: This callback must NOT await Supabase queries. The auth client
    // awaits all subscriber callbacks during initialization via _notifyAllSubscribers.
    // If the callback awaits a Supabase query, that query calls getSession() which
    // awaits initializePromise — creating a circular deadlock.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (cancelled) return;
        setUser(session?.user ?? null);
        if (session?.user) {
          fetchProfile(session.user.id);
          fetchRoles(session.user.id);
        } else {
          setProfile(null);
          setRoles([]);
          setActiveRole(null);
          setLoading(false);
        }
      }
    );

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchProfile(userId) {
    const { data, error } = await withRetry(() =>
      supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single()
    );

    if (!error && data) {
      registerDemoUser(data.id, data.email);
      applyDemoPhoto(data);
      setProfile(data);

      // Register all demo users so join-query photo overrides work
      const demoEmails = ['tom@vouch-demo.com', 'tracey@vouch-demo.com', 'kevin@vouch-demo.com'];
      const otherEmails = demoEmails.filter(e => e !== data.email);
      if (otherEmails.length > 0) {
        const { data: others } = await withRetry(() =>
          supabase.from('profiles').select('id, email').in('email', otherEmails)
        );
        if (others) others.forEach(p => registerDemoUser(p.id, p.email));
      }
    }
  }

  async function fetchRoles(userId) {
    const { data, error } = await withRetry(() =>
      supabase
        .from('user_roles')
        .select('*')
        .eq('user_id', userId)
        .eq('is_active', true)
    );

    if (!error && data) {
      const roleList = data.map(r => r.role);
      setRoles(roleList);
      // Set active role to first role if none selected.
      // Use functional update to read actual current value (closure is stale).
      if (roleList.length > 0) {
        setActiveRole(prev => prev || roleList[0]);
      }
    }
    setLoading(false);
  }

  async function signUp(email, password, displayName, role) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { display_name: displayName, initial_role: role }
      }
    });

    if (error) throw error;

    // If no session returned, email confirmation may be required
    // or the email is already registered (Supabase returns fake success for privacy)
    if (!data.session) {
      throw new Error(
        'Unable to create account. This email may already be registered. Try signing in instead.'
      );
    }

    // Profile and role are created by database trigger on auth.users insert
    // But we also insert the role explicitly in case the trigger only creates the profile
    if (data.user) {
      await supabase.from('user_roles').upsert({
        user_id: data.user.id,
        role: role,
        is_active: true
      }, { onConflict: 'user_id,role' });
    }

    // Set active role immediately so the user doesn't land on "Welcome" screen
    setActiveRole(role);

    return data;
  }

  async function signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) throw error;
    return data;
  }

  async function signOut() {
    // Always clear local state, even if the Supabase call fails
    try {
      await supabase.auth.signOut();
    } catch (_) {
      // Ignore — we still want to clear local state
    }
    setUser(null);
    setProfile(null);
    setRoles([]);
    setActiveRole(null);
  }

  function switchRole(role) {
    setActiveRole(role);
  }

  async function addRole(role) {
    if (!user) return;
    const { error } = await withRetry(() =>
      supabase.from('user_roles').upsert({
        user_id: user.id,
        role: role,
        is_active: true
      }, { onConflict: 'user_id,role' })
    );

    if (!error) {
      if (!roles.includes(role)) {
        setRoles(prev => [...prev, role]);
      }
      // Also set active role immediately to avoid race condition
      // (switchRole may run before setRoles has been applied)
      setActiveRole(role);
    }
    return { error };
  }

  const value = {
    user,
    profile,
    roles,
    activeRole,
    loading,
    signUp,
    signIn,
    signOut,
    switchRole,
    addRole,
    refreshProfile: () => user && fetchProfile(user.id),
    refreshRoles: () => user && fetchRoles(user.id),
    updateProfileState: (updates) => setProfile(prev => prev ? { ...prev, ...updates } : prev),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
